import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  getTradingOrdersOrderId,
  postTradingOrdersOrderIdCancel,
} from "../api/endpoints/trading/trading";
import { getWalletHistoryId } from "../api/endpoints/wallet/wallet";
import type { SwapOrderDetails } from "../api/models/swapOrderDetails";
import type { TradingOrderDetails } from "../api/models/tradingOrderDetails";
import type { WalletHistoryDetails } from "../api/models/walletHistoryDetails";
import { getBearerToken } from "../lib/authClient";

import "./OrderDetailPage.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

const STATUS_LABELS: Record<string, string> = {
  CREATED: "Criada",
  AWAITING_PAYMENT: "Aguardando pagamento",
  PROCESSING: "Processando",
  CONFIRMED: "Confirmada",
  FAILED: "Falhou",
  EXPIRED: "Expirada",
  NEW: "Aberta",
  PARTIALLY_FILLED: "Parcialmente executada",
  FILLED: "Executada",
  CANCELLED: "Cancelada",
  REJECTED: "Rejeitada",
};

const TERMINAL_STATUSES = new Set([
  "CONFIRMED", "FAILED", "EXPIRED", "CANCELLED", "FILLED", "REJECTED",
]);

// Produtos de trading cujas ordens podem ser canceladas via /trading/orders/:id/cancel
const TRADING_PRODUCTS = new Set(["TRADE_SPOT", "TRADE_SWAP", "TRADE_LINEAR"]);

// Status de uma ordem de trading (wallet/history) que ainda permitem cancelamento
const CANCELABLE_HISTORY_STATUSES = new Set(["CREATED", "PROCESSING"]);

// Status da rota legada (trading/orders) que ainda permitem cancelamento
const CANCELABLE_ORDER_STATUSES = new Set(["NEW", "PARTIALLY_FILLED"]);

const ORDER_TYPE_LABELS: Record<string, string> = {
  MARKET: "Mercado",
  LIMIT: "Limite",
};

const PRODUCT_LABELS: Record<string, string> = {
  SWAP: "Swap",
  TRADE_SWAP: "Swap",
  SPOT: "Spot",
  TRADE_SPOT: "Spot",
  LINEAR: "Futuros",
  TRADE_LINEAR: "Futuros",
};

const SIDE_LABELS: Record<string, string> = {
  BUY: "Compra",
  SELL: "Venda",
  DEPOSIT: "Depósito",
  WITHDRAW: "Retirada",
};

const formatCryptoAmount = (value: string | null | undefined) => {
  if (!value) return "—";
  const n = Number(value);
  if (isNaN(n)) return value;
  if (n < 0.0001) return n.toFixed(10).replace(/0+$/, "");
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 8 });
};

const formatFiatAmount = (value: string | null | undefined) => {
  if (!value) return "—";
  const n = Number(value);
  if (isNaN(n)) return value;
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams<{ historyId?: string; orderId?: string }>();
  const historyId = params.historyId ?? "";
  const legacyOrderId = params.orderId ?? "";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelDone, setCancelDone] = useState(false);

  const fetchItem = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) setLoading(true);
    setError(null);
    try {
      if (historyId) {
        const response = await getWalletHistoryId(historyId, authHeaders());
        if (response.status === 200 && "data" in response.data) {
          setItem(response.data.data);
        } else {
          const msg = (response.data as { message?: string })?.message ??
            `Falha ao buscar histórico (status ${response.status}).`;
          if (!silent) setError(msg);
        }
      } else if (legacyOrderId) {
        const response = await getTradingOrdersOrderId(legacyOrderId, authHeaders());
        if (response.status === 200 && "data" in response.data) {
          setItem(response.data.data);
        } else {
          const msg = (response.data as { message?: string })?.message ??
            `Falha ao buscar ordem (status ${response.status}).`;
          if (!silent) setError(msg);
        }
      }
    } catch (err) {
      console.error("Erro ao buscar detalhe:", err);
      if (!silent) {
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [historyId, legacyOrderId]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  const rawStatus: string = item?.orderStatus ?? item?.status ?? "";
  const isTerminal = TERMINAL_STATUSES.has(rawStatus);

  // Polling apenas para rota legada enquanto não terminal
  useEffect(() => {
    if (!legacyOrderId || isTerminal || !item) return;
    const handle = window.setInterval(() => {
      fetchItem({ silent: true });
    }, 2000);
    return () => window.clearInterval(handle);
  }, [legacyOrderId, isTerminal, item, fetchItem]);

  // Em wallet/history o id da ordem de trading é o internalId (UUID de trading_orders);
  // na rota legada é o próprio orderId.
  const cancelableId: string = historyId
    ? item?.internalId ?? ""
    : item?.referenceId ?? item?.orderId ?? "";

  const canCancel = historyId
    ? TRADING_PRODUCTS.has(item?.product) &&
      CANCELABLE_HISTORY_STATUSES.has(rawStatus) &&
      !!cancelableId
    : !isTerminal &&
      CANCELABLE_ORDER_STATUSES.has(rawStatus) &&
      !!cancelableId;

  const handleCancel = async () => {
    if (!cancelableId) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const response = await postTradingOrdersOrderIdCancel(cancelableId, authHeaders());
      if (response.status === 200) {
        setCancelDone(true);
        await fetchItem({ silent: true });
      } else {
        const msg = (response.data as { message?: string })?.message ??
          `Falha ao cancelar (status ${response.status}).`;
        setCancelError(msg);
      }
    } catch (err) {
      console.error("Erro ao cancelar:", err);
      setCancelError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setCancelling(false);
    }
  };

  const statusClass = `orderDetail-statusPill orderDetail-statusPill--${rawStatus.toLowerCase()}`;

  const isHistoryItem = !!historyId;
  const historyDetails: WalletHistoryDetails | null =
    isHistoryItem && item?.details && typeof item.details === "object"
      ? (item.details as WalletHistoryDetails)
      : null;

  const swapDetails: SwapOrderDetails | null =
    historyDetails && "fromCoin" in historyDetails ? historyDetails : null;

  // Detalhes de ordens de trading (TRADE_SPOT/SWAP/LINEAR) — presença de `symbol`/`orderType`
  const tradeDetails: TradingOrderDetails | null =
    historyDetails && "symbol" in historyDetails ? historyDetails : null;

  const displayPair = isHistoryItem
    ? swapDetails?.fromCoin && swapDetails?.toCoin
      ? `${swapDetails.fromCoin} → ${swapDetails.toCoin}`
      : tradeDetails?.symbol ?? (item?.coin ?? "—")
    : item
    ? `${item.baseCoin}/${item.quoteCoin}`
    : "—";

  const displaySide: string = item?.side ?? "";
  const displayProduct: string = item?.product ?? "";
  const displayStatus = STATUS_LABELS[rawStatus] ?? rawStatus;

  return (
    <div className="page-centered">
      <div className="orderDetail-wrap">
        <button
          type="button"
          className="orderDetail-back"
          onClick={() => navigate(-1)}
        >
          ← Voltar
        </button>

        {loading && (
          <div className="orderDetail-status">Carregando...</div>
        )}

        {error && !loading && (
          <div className="orderDetail-status orderDetail-status--error">
            {error}
          </div>
        )}

        {item && !loading && (
          <>
            <div className="orderDetail-head">
              <div className="orderDetail-headTitle">
                <span className="orderDetail-pair">{displayPair}</span>
                {displaySide && (
                  <span
                    className={`orderDetail-sidePill orderDetail-sidePill--${displaySide.toLowerCase()}`}
                  >
                    {SIDE_LABELS[displaySide] ?? displaySide}
                  </span>
                )}
              </div>
              <div className={statusClass}>
                {displayStatus}
                {!isTerminal && (
                  <span className="orderDetail-pollingDot" aria-hidden="true">
                    ●
                  </span>
                )}
              </div>
            </div>

            <div className="orderDetail-card">
              {displayProduct && (
                <div className="orderDetail-row">
                  <span>Produto</span>
                  <span>{PRODUCT_LABELS[displayProduct] ?? displayProduct}</span>
                </div>
              )}

              {/* Campos do wallet/history */}
              {isHistoryItem && (
                <>
                  <div className="orderDetail-row">
                    <span>ID</span>
                    <span className="orderDetail-mono">
                      {item.internalId ?? item.id}
                    </span>
                  </div>
                  {item.externalId && (
                    <div className="orderDetail-row">
                      <span>ID externo</span>
                      <span className="orderDetail-mono">{item.externalId}</span>
                    </div>
                  )}

                  {/* Detalhes do swap */}
                  {swapDetails?.fromCoin ? (
                    <>
                      <div className="orderDetail-swapConversion">
                        <div className="orderDetail-swapLeg">
                          <div className="orderDetail-swapLegLabel">Pago</div>
                          <div className="orderDetail-swapLegAmount">
                            {formatFiatAmount(swapDetails.fromAmount)}
                          </div>
                          <div className="orderDetail-swapLegCoin">
                            {swapDetails.fromCoin}
                          </div>
                        </div>
                        <div className="orderDetail-swapArrow">→</div>
                        <div className="orderDetail-swapLeg">
                          <div className="orderDetail-swapLegLabel">Recebido</div>
                          <div className="orderDetail-swapLegAmount">
                            {formatCryptoAmount(swapDetails.toAmount)}
                          </div>
                          <div className="orderDetail-swapLegCoin">
                            {swapDetails.toCoin}
                          </div>
                        </div>
                      </div>

                      {swapDetails.fromAmount && swapDetails.toAmount &&
                        Number(swapDetails.toAmount) > 0 && (
                          <div className="orderDetail-row">
                            <span>Preço efetivo</span>
                            <span>
                              {(
                                Number(swapDetails.fromAmount) /
                                Number(swapDetails.toAmount)
                              ).toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              {swapDetails.fromCoin}/{swapDetails.toCoin}
                            </span>
                          </div>
                        )}

                      {swapDetails.quoteTxId && (
                        <div className="orderDetail-row">
                          <span>Quote TX ID</span>
                          <span className="orderDetail-mono">
                            {swapDetails.quoteTxId}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="orderDetail-row">
                      <span>{tradeDetails ? "Quantidade" : "Valor"}</span>
                      <span>
                        {formatCryptoAmount(item.amount)} {item.coin}
                      </span>
                    </div>
                  )}

                  {/* Detalhes da ordem de trading */}
                  {tradeDetails && (
                    <>
                      {tradeDetails.orderType && (
                        <div className="orderDetail-row">
                          <span>Tipo de ordem</span>
                          <span>
                            {ORDER_TYPE_LABELS[tradeDetails.orderType] ??
                              tradeDetails.orderType}
                          </span>
                        </div>
                      )}
                      {tradeDetails.limitPrice &&
                        Number(tradeDetails.limitPrice) > 0 && (
                          <div className="orderDetail-row">
                            <span>Preço limite</span>
                            <span>
                              {formatFiatAmount(tradeDetails.limitPrice)}{" "}
                              {tradeDetails.quoteCoin ?? ""}
                            </span>
                          </div>
                        )}
                      {tradeDetails.leavesQty &&
                        Number(tradeDetails.leavesQty) > 0 && (
                          <div className="orderDetail-row">
                            <span>Quantidade pendente</span>
                            <span>
                              {formatCryptoAmount(tradeDetails.leavesQty)}{" "}
                              {item.coin}
                            </span>
                          </div>
                        )}
                      {tradeDetails.cumExecValue &&
                        Number(tradeDetails.cumExecValue) > 0 && (
                          <div className="orderDetail-row">
                            <span>Valor executado</span>
                            <span>
                              {formatFiatAmount(tradeDetails.cumExecValue)}{" "}
                              {tradeDetails.quoteCoin ?? ""}
                            </span>
                          </div>
                        )}
                      {tradeDetails.cumExecFee &&
                        Number(tradeDetails.cumExecFee) > 0 && (
                          <div className="orderDetail-row">
                            <span>Taxa acumulada</span>
                            <span>
                              {formatCryptoAmount(tradeDetails.cumExecFee)}{" "}
                              {tradeDetails.quoteCoin ?? ""}
                            </span>
                          </div>
                        )}

                      {/* Campos persistidos de futuros lineares (TRADE_LINEAR) */}
                      {tradeDetails.leverage && (
                        <div className="orderDetail-row">
                          <span>Alavancagem</span>
                          <span>
                            {tradeDetails.leverage}x
                            {tradeDetails.marginMode
                              ? ` · ${tradeDetails.marginMode}`
                              : ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.initialMargin && (
                        <div className="orderDetail-row">
                          <span>Margem inicial</span>
                          <span>
                            {formatFiatAmount(tradeDetails.initialMargin)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.entryPrice && (
                        <div className="orderDetail-row">
                          <span>Preço de entrada</span>
                          <span>
                            {formatFiatAmount(tradeDetails.entryPrice)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.liqPrice && (
                        <div className="orderDetail-row">
                          <span>Preço de liquidação</span>
                          <span>
                            {formatFiatAmount(tradeDetails.liqPrice)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.takeProfit && (
                        <div className="orderDetail-row">
                          <span>Take profit</span>
                          <span>
                            {formatFiatAmount(tradeDetails.takeProfit)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.stopLoss && (
                        <div className="orderDetail-row">
                          <span>Stop loss</span>
                          <span>
                            {formatFiatAmount(tradeDetails.stopLoss)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                      {tradeDetails.realizedPnl && (
                        <div className="orderDetail-row">
                          <span>P&L realizado</span>
                          <span
                            className={Number(tradeDetails.realizedPnl) >= 0
                              ? "orderDetail-pnl--positive"
                              : "orderDetail-pnl--negative"}
                          >
                            {formatFiatAmount(tradeDetails.realizedPnl)}{" "}
                            {tradeDetails.quoteCoin ?? ""}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {item.avgPrice && Number(item.avgPrice) > 0 && (
                    <div className="orderDetail-row">
                      <span>Preço médio</span>
                      <span>
                        {formatFiatAmount(item.avgPrice)}{" "}
                        {swapDetails?.fromCoin ?? tradeDetails?.quoteCoin ?? ""}
                      </span>
                    </div>
                  )}
                  {item.fee ? (
                    <div className="orderDetail-row">
                      <span>Taxa</span>
                      <span>
                        {formatCryptoAmount(item.fee)} {item.feeCoin ?? ""}
                      </span>
                    </div>
                  ) : swapDetails?.fromCoin ? (
                    <div className="orderDetail-row">
                      <span>Taxa</span>
                      <span className="orderDetail-muted">Incluída no preço</span>
                    </div>
                  ) : null}
                  {item.fiatAmount && (
                    <div className="orderDetail-row">
                      <span>Valor fiat</span>
                      <span>
                        {formatFiatAmount(item.fiatAmount)}{" "}
                        {item.fiatCurrency ?? ""}
                      </span>
                    </div>
                  )}
                  {item.network && (
                    <div className="orderDetail-row">
                      <span>Rede</span>
                      <span>{item.network}</span>
                    </div>
                  )}
                  {item.txHash && (
                    <div className="orderDetail-row">
                      <span>Tx Hash</span>
                      <span className="orderDetail-mono">{item.txHash}</span>
                    </div>
                  )}
                  <div className="orderDetail-row">
                    <span>Criada em</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                  {item.confirmedAt && (
                    <div className="orderDetail-row">
                      <span>Confirmada em</span>
                      <span>{formatDateTime(item.confirmedAt)}</span>
                    </div>
                  )}
                  {item.updatedAt && item.updatedAt !== item.confirmedAt && (
                    <div className="orderDetail-row">
                      <span>Atualizada em</span>
                      <span>{formatDateTime(item.updatedAt)}</span>
                    </div>
                  )}
                </>
              )}

              {/* Campos do trading/orders (legado) */}
              {!isHistoryItem && (
                <>
                  <div className="orderDetail-row">
                    <span>ID da ordem</span>
                    <span className="orderDetail-mono">{item.orderId}</span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Tipo</span>
                    <span>
                      {ORDER_TYPE_LABELS[item.orderType] ?? item.orderType}
                    </span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Quantidade</span>
                    <span>
                      {item.qty} {item.baseCoin}
                    </span>
                  </div>
                  {item.price && (
                    <div className="orderDetail-row">
                      <span>Preço</span>
                      <span>
                        {item.price} {item.quoteCoin}
                      </span>
                    </div>
                  )}
                  <div className="orderDetail-row">
                    <span>Executado</span>
                    <span>
                      {item.cumExecQty} {item.baseCoin}
                    </span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Pendente</span>
                    <span>
                      {item.leavesQty} {item.baseCoin}
                    </span>
                  </div>
                  {item.avgPrice && (
                    <div className="orderDetail-row">
                      <span>Preço médio</span>
                      <span>
                        {item.avgPrice} {item.quoteCoin}
                      </span>
                    </div>
                  )}
                  <div className="orderDetail-row">
                    <span>Valor executado</span>
                    <span>
                      {item.cumExecValue} {item.quoteCoin}
                    </span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Taxa acumulada</span>
                    <span>
                      {item.cumExecFee} {item.quoteCoin}
                    </span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Criada em</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                  <div className="orderDetail-row">
                    <span>Atualizada em</span>
                    <span>{formatDateTime(item.updatedAt)}</span>
                  </div>
                  {item.executions?.length > 0 && (
                    <>
                      <div className="orderDetail-sectionTitle">Execuções</div>
                      {item.executions.map(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (exec: any) => (
                          <div key={exec.execId} className="orderDetail-exec">
                            <div className="orderDetail-execRow">
                              <span className="orderDetail-mono">
                                {exec.execId}
                              </span>
                              <span>{formatDateTime(exec.execTime)}</span>
                            </div>
                            <div className="orderDetail-execRow">
                              <span>
                                {exec.execQty} {item.baseCoin}
                              </span>
                              <span>
                                @ {exec.execPrice} {item.quoteCoin}
                              </span>
                            </div>
                            <div className="orderDetail-execRow orderDetail-execRow--muted">
                              <span>
                                Valor: {exec.execValue} {item.quoteCoin}
                              </span>
                              <span>
                                Taxa: {exec.execFee} {exec.feeCoin}
                              </span>
                            </div>
                          </div>
                        ),
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {cancelError && (
              <div className="orderDetail-status orderDetail-status--error">
                {cancelError}
              </div>
            )}

            {cancelDone && rawStatus === "CANCELLED" && (
              <div className="orderDetail-cancelBanner" role="status">
                <span className="orderDetail-cancelBannerIcon" aria-hidden="true">
                  ✓
                </span>
                <div className="orderDetail-cancelBannerText">
                  <strong>Ordem cancelada</strong>
                  <span>O cancelamento foi confirmado com sucesso.</span>
                </div>
              </div>
            )}

            <div className="orderDetail-actions">
              <button
                type="button"
                className="orderDetail-btn orderDetail-btn--primary"
                onClick={() => fetchItem()}
              >
                Atualizar
              </button>
              {isHistoryItem && (
                <button
                  type="button"
                  className="orderDetail-btn orderDetail-btn--secondary"
                  onClick={() => navigate("/wallet/history")}
                >
                  Ver histórico
                </button>
              )}
              {isHistoryItem && item.product === "TRADE_LINEAR" &&
                tradeDetails?.bybitOrderId && (
                <button
                  type="button"
                  className="orderDetail-btn orderDetail-btn--secondary"
                  onClick={() =>
                    navigate(`/futures/positions/${tradeDetails.bybitOrderId}`)}
                >
                  Ver posição
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  className="orderDetail-btn orderDetail-btn--danger"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelando..." : "Cancelar ordem"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
